// Department Calendar Component - Responsive Layout
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/resizable";
import { Calendar, ChevronLeft, ChevronRight, Plus, Users, Clock, User, Play, Square, Timer, X, CheckSquare, Layers, Eye, FileImage } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useSearchParams } from "react-router-dom";
import { 
  getAllDepartments, 
  getWorkItemsByDepartment,
  getDepartmentSummary,
  getOrderById,
  getWorkItemsByOrder
} from "@/utils/dataManager";
import { workItems } from "@/data/mockData";
import { orders } from "@/data/masterData";

const DepartmentCalendar = () => {
  const [searchParams] = useSearchParams();
  
  // 使用統一資料管理
  const allDepartments = getAllDepartments();
  
  const [selectedDepartment, setSelectedDepartment] = useState(() => {
    // 使用第一個可用部門作為預設值
    return allDepartments.length > 0 ? allDepartments[0].name : "採購部";
  });
  const [selectedProcess, setSelectedProcess] = useState("all");
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [selectedWorkItem, setSelectedWorkItem] = useState<typeof workItems[0] | null>(null);
  const [activeTimers, setActiveTimers] = useState<{[key: string]: boolean}>({});
  const [startTimes, setStartTimes] = useState<{[key: string]: Date}>({});
  const [showDetailsPanel, setShowDetailsPanel] = useState(false);
  
  // 批次選擇模式狀態
  const [batchMode, setBatchMode] = useState(false);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [currentOperationType, setCurrentOperationType] = useState<string | null>(null);
  const [showBatchConfirm, setShowBatchConfirm] = useState(false);
  
  // 批次處理狀態
  const [batchNotes, setBatchNotes] = useState<{[batchId: string]: string}>({});
  const [showBatchPanel, setShowBatchPanel] = useState(false); // 控制是否顯示批次面板UI
  const [batchGroups, setBatchGroups] = useState<{[batchId: string]: string[]}>({});  // 記錄批次群組：{batchId: [itemId1, itemId2, ...]}
  
  // 圖面顯示狀態
  const [showDrawings, setShowDrawings] = useState(false);
  const [selectedDrawingIndex, setSelectedDrawingIndex] = useState(0);
  
  // 實作數量狀態
  const [actualQuantities, setActualQuantities] = useState<{[workItemId: string]: number}>({});

  // 檢查 URL 參數中是否有指定部門
  useEffect(() => {
    const deptParam = searchParams.get('department');
    if (deptParam) {
      setSelectedDepartment(deptParam);
    }
  }, [searchParams]);

  // 當部門改變時，重置工序篩選
  useEffect(() => {
    setSelectedProcess("all");
  }, [selectedDepartment]);

  // 生成5天的日期，今天在中間 (桌面版) 或 單日 (手機版)
  const generateWeekDates = (startDate: Date) => {
    const dates = [];
    const today = new Date(startDate);
    
    // 今天在中間，前後各2天
    for (let i = -2; i <= 2; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  // 生成單日 (手機版)
  const generateSingleDate = (date: Date) => {
    return [new Date(date)];
  };

  const weekDates = generateWeekDates(currentWeek); // 桌面版 5 天
  const singleDate = generateSingleDate(currentWeek); // 手機版 1 天
  
  // 使用統一資料管理取得工作項目
  const departmentWorkItems = getWorkItemsByDepartment(selectedDepartment);
  
  // 取得當前部門的所有工序
  const getAvailableProcesses = () => {
    const processes = new Set<string>();
    departmentWorkItems.forEach(item => {
      processes.add(item.title);
    });
    return Array.from(processes).sort();
  };
  
  // 根據工序篩選工作項目
  const filteredWorkItems = selectedProcess === "all" 
    ? departmentWorkItems 
    : departmentWorkItems.filter(item => item.title === selectedProcess);
  
  // 獲取指定日期的工作項目
  const getWorkItemsForDateFiltered = (date: Date) => {
    const dateStr = date.toISOString().split('T')[0];
    return filteredWorkItems.filter(item => item.date === dateStr);
  };

  // 取得部門摘要
  const departmentSummary = getDepartmentSummary(selectedDepartment, new Date());

  // 導航週別
  const navigateWeek = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentWeek);
    newDate.setDate(currentWeek.getDate() + (direction === 'prev' ? -7 : 7));
    setCurrentWeek(newDate);
  };

  // Handle work item selection
  const handleWorkItemSelect = (item: typeof workItems[0]) => {
    // 總是允許選擇工作項目
    setSelectedWorkItem(item);
    setShowDetailsPanel(true);
    
    // 檢查選中的項目是否屬於批次處理
    const itemInBatch = isItemInBatch(item.id);
    setShowBatchPanel(itemInBatch);
  };

  // Handle close details panel
  const handleCloseDetails = () => {
    setShowDetailsPanel(false);
    setSelectedWorkItem(null);
    setShowBatchPanel(false); // 關閉批次面板UI，但保持批次項目繼續計時
    // 關閉面板後，確保回到週視圖（清除單項選擇狀態）
  };

  // Timer functions
  const toggleTimer = (itemId: string) => {
    setActiveTimers(prev => {
      const newTimers = { ...prev };
      if (newTimers[itemId]) {
        // Stop timer
        delete newTimers[itemId];
        delete startTimes[itemId];
      } else {
        // Start timer
        newTimers[itemId] = true;
        setStartTimes(prev => ({ ...prev, [itemId]: new Date() }));
      }
      return newTimers;
    });
  };

  // 批次選擇相關函數
  const getOperationType = (item: typeof workItems[0]) => {
    // 從工作項目標題中提取工序類型
    const title = item.title.toLowerCase();
    if (title.includes('粗車')) return 'rough_turning';
    if (title.includes('精加工') || title.includes('精車')) return 'fine_machining';
    if (title.includes('包膠')) return 'rubber_coating';
    if (title.includes('除膠')) return 'degumming';
    if (title.includes('研磨')) return 'grinding';
    if (title.includes('檢驗')) return 'inspection';
    if (title.includes('組裝')) return 'assembly';
    return 'other';
  };

  const getOperationDisplayName = (operationType: string) => {
    const typeMap: {[key: string]: string} = {
      'rough_turning': '粗車',
      'fine_machining': '精加工',
      'rubber_coating': '包膠', 
      'degumming': '除膠',
      'grinding': '研磨',
      'inspection': '檢驗',
      'assembly': '組裝',
      'other': '其他'
    };
    return typeMap[operationType] || '未知工序';
  };

  const canSelectItem = (item: typeof workItems[0]) => {
    if (!batchMode) return false;
    if (item.status === 'completed') return false;
    if (activeTimers[item.id]) return false;
    
    const itemType = getOperationType(item);
    if (!currentOperationType) return true;
    return itemType === currentOperationType;
  };

  const handleItemSelect = (item: typeof workItems[0], checked: boolean) => {
    const itemType = getOperationType(item);
    
    if (checked) {
      // 選中項目
      if (!currentOperationType) {
        setCurrentOperationType(itemType);
      }
      setSelectedItems(prev => new Set([...prev, item.id]));
    } else {
      // 取消選中
      const newSelectedItems = new Set(selectedItems);
      newSelectedItems.delete(item.id);
      setSelectedItems(newSelectedItems);
      
      // 如果沒有選中項目了，清空工序類型
      if (newSelectedItems.size === 0) {
        setCurrentOperationType(null);
      }
    }
  };

  const clearSelection = () => {
    setSelectedItems(new Set());
    setCurrentOperationType(null);
  };

  // 獲取項目所屬的批次ID
  const getItemBatchId = (itemId: string): string | null => {
    for (const [batchId, itemIds] of Object.entries(batchGroups)) {
      if (itemIds.includes(itemId)) {
        return batchId;
      }
    }
    return null;
  };

  // 檢查項目是否屬於批次處理
  const isItemInBatch = (itemId: string): boolean => {
    return getItemBatchId(itemId) !== null;
  };

  const handleBatchStart = () => {
    if (selectedItems.size === 0) return;
    setShowBatchConfirm(true);
  };

  const confirmBatchStart = () => {
    // 批次開始所有選中的項目
    const newTimers = { ...activeTimers };
    const newStartTimes = { ...startTimes };
    const batchId = `batch_${Date.now()}_${currentOperationType}`;
    const currentTime = new Date();
    const selectedItemsArray = Array.from(selectedItems);
    
    selectedItems.forEach(itemId => {
      newTimers[itemId] = true;
      newStartTimes[itemId] = currentTime;
    });
    
    setActiveTimers(newTimers);
    setStartTimes(newStartTimes);
    // setBatchStartTime(prev => ({ ...prev, [batchId]: currentTime }));
    
    // 記錄批次群組
    setBatchGroups(prev => ({ ...prev, [batchId]: selectedItemsArray }));
    
    // 選擇第一個批次項目作為當前選中項目
    const firstSelectedItem = filteredWorkItems.find(item => selectedItems.has(item.id));
    if (firstSelectedItem) {
      setSelectedWorkItem(firstSelectedItem);
    }
    
    // 清空選擇並關閉對話框
    clearSelection();
    setShowBatchConfirm(false);
    setBatchMode(false);
    
    // 自動打開批次處理面板
    setShowBatchPanel(true);
    setShowDetailsPanel(true);
  };

  const getSelectedItemsDetails = () => {
    return filteredWorkItems.filter(item => selectedItems.has(item.id));
  };

  // 獲取當前批次處理的項目 (基於選中的項目)
  const getCurrentBatchItems = () => {
    if (!selectedWorkItem) return [];
    
    const batchId = getItemBatchId(selectedWorkItem.id);
    if (!batchId) return [];
    
    const batchItemIds = batchGroups[batchId] || [];
    return filteredWorkItems.filter(item => 
      batchItemIds.includes(item.id) && activeTimers[item.id]
    );
  };

  // 獲取批次處理的日期（第一筆訂單的日期）
  const getBatchDate = () => {
    const batchItems = getCurrentBatchItems();
    if (batchItems.length > 0) {
      return new Date(batchItems[0].date);
    }
    return new Date();
  };

  // 檢查是否有批次處理正在進行 (基於當前選中項目)
  const hasBatchProcessing = () => {
    if (!selectedWorkItem) return false;
    return isItemInBatch(selectedWorkItem.id) && getCurrentBatchItems().length > 1;
  };

  // 獲取批次工序類型
  const getCurrentBatchOperationType = () => {
    const batchItems = getCurrentBatchItems();
    return batchItems.length > 0 ? getOperationType(batchItems[0]) : null;
  };

  // 批次備註更新
  const updateBatchNotes = (notes: string) => {
    const batchItems = getCurrentBatchItems();
    if (batchItems.length > 0) {
      const batchId = `batch_${getCurrentBatchOperationType()}`;
      setBatchNotes(prev => ({ ...prev, [batchId]: notes }));
      
      // 這裡可以調用API同步更新所有批次項目的備註
      console.log('Updating batch notes for items:', batchItems.map(item => item.id), 'Notes:', notes);
    }
  };

  // 批次暫停
  const pauseBatch = () => {
    if (!selectedWorkItem) return;
    
    const batchId = getItemBatchId(selectedWorkItem.id);
    if (!batchId) return;
    
    const batchItems = getCurrentBatchItems();
    const newTimers = { ...activeTimers };
    
    batchItems.forEach(item => {
      delete newTimers[item.id];
    });
    
    setActiveTimers(newTimers);
    
    // 暫停時不清理批次群組，只是停止計時
    // 批次群組記錄保留，以便之後可以重新開始
  };

  // 批次完成
  const completeBatch = () => {
    if (!selectedWorkItem) return;
    
    const batchId = getItemBatchId(selectedWorkItem.id);
    if (!batchId) return;
    
    const batchItems = getCurrentBatchItems();
    const newTimers = { ...activeTimers };
    
    batchItems.forEach(item => {
      delete newTimers[item.id];
    });
    
    setActiveTimers(newTimers);
    setShowDetailsPanel(false);
    setShowBatchPanel(false);
    setSelectedWorkItem(null); // 清除選中的工作項目，以回到週視圖
    
    // 清理批次群組記錄
    setBatchGroups(prev => {
      const newGroups = { ...prev };
      delete newGroups[batchId];
      return newGroups;
    });
    
    // 這裡可以調用API標記批次完成
    console.log('Batch completed for items:', batchItems.map(item => item.id));
  };

  return (
    <div className="h-screen flex flex-col">
      {/* Header Section */}
      <div className="p-3 md:p-6 space-y-4 md:space-y-6 flex-shrink-0">
        {/* 標題區域 */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          <div className="min-w-0">
            <h1 className="text-xl md:text-3xl font-bold tracking-tight">部門日曆</h1>
            <p className="text-muted-foreground text-sm md:text-base">
              查看各部門的工作安排和排程
            </p>
          </div>
          
          <div className="flex flex-col md:flex-row gap-2 w-full md:w-auto">
            <Button 
              variant={batchMode ? "default" : "outline"}
              onClick={() => {
                setBatchMode(!batchMode);
                if (batchMode) {
                  clearSelection();
                }
              }}
              className="min-h-[44px] w-full md:w-auto"
            >
              <Layers className="mr-2 h-4 w-4" />
              {batchMode ? "退出批次" : "批次模式"}
            </Button>
            
            <Button className="min-h-[44px] w-full md:w-auto">
              <Plus className="mr-2 h-4 w-4" />
              新增工作項目
            </Button>
          </div>
        </div>

        {/* 控制區域 */}
        <div className="flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium">部門:</label>
              <Select value={selectedDepartment} onValueChange={setSelectedDepartment}>
                <SelectTrigger className="w-full sm:w-[200px] min-h-[44px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {allDepartments.map((dept) => (
                    <SelectItem key={dept.id} value={dept.name}>
                      <div className="flex items-center space-x-2">
                        <div className={`w-2 h-2 rounded-full ${dept.color}`}></div>
                        <span>{dept.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium">工序:</label>
              <Select value={selectedProcess} onValueChange={setSelectedProcess}>
                <SelectTrigger className="w-full sm:w-[200px] min-h-[44px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 rounded-full bg-gray-400"></div>
                      <span>全部工序</span>
                    </div>
                  </SelectItem>
                  {getAvailableProcesses().map((process) => (
                    <SelectItem key={process} value={process}>
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                        <span>{process}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* 週別導航 - 桌面版 */}
          <div className="hidden md:flex items-center justify-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigateWeek('prev')}
              className="min-h-[44px]"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-xs sm:text-sm font-medium px-2 sm:px-4 text-center">
              {weekDates[0].toLocaleDateString('zh-TW', { month: 'long', day: 'numeric' })} - {weekDates[4].toLocaleDateString('zh-TW', { month: 'long', day: 'numeric' })}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigateWeek('next')}
              className="min-h-[44px]"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          {/* 日期導航 - 手機版 */}
          <div className="md:hidden flex items-center justify-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const newDate = new Date(currentWeek);
                newDate.setDate(currentWeek.getDate() - 1);
                setCurrentWeek(newDate);
              }}
              className="min-h-[44px]"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm font-medium px-4 text-center min-w-[120px]">
              {currentWeek.toLocaleDateString('zh-TW', { 
                month: 'long', 
                day: 'numeric',
                weekday: 'short'
              })}
              {currentWeek.toDateString() === new Date().toDateString() && (
                <span className="ml-2 text-xs text-primary font-medium">(今天)</span>
              )}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const newDate = new Date(currentWeek);
                newDate.setDate(currentWeek.getDate() + 1);
                setCurrentWeek(newDate);
              }}
              className="min-h-[44px]"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* 部門摘要 */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs md:text-sm font-medium">今日工作項目</CardTitle>
              <Calendar className="h-3 w-3 md:h-4 md:w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent className="pb-3">
              <div className="text-xl md:text-2xl font-bold">{departmentSummary.workItemsCount}</div>
              <p className="text-xs text-muted-foreground">
                今日安排
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">可用人力</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{departmentSummary.availableWorkers}</div>
              <p className="text-xs text-muted-foreground">
                人員
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">預估工時</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{departmentSummary.estimatedHours}</div>
              <p className="text-xs text-muted-foreground">
                小時
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">本週總項目</CardTitle>
              <Calendar className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{filteredWorkItems.length}</div>
              <p className="text-xs text-muted-foreground">
                全週安排
              </p>
            </CardContent>
          </Card>
        </div>
        
        {/* 批次操作工具列 */}
        {batchMode && (
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-3">
                <CheckSquare className="h-5 w-5 text-primary" />
                <div>
                  <div className="font-medium text-sm">
                    {selectedItems.size > 0 ? (
                      <>已選 {selectedItems.size} 項 "{getOperationDisplayName(currentOperationType || '')}" 工序</>
                    ) : (
                      "批次模式已啟用 - 選擇相同工序的項目"
                    )}
                  </div>
                  {selectedItems.size > 0 && (
                    <div className="text-xs text-muted-foreground mt-1">
                      只能選擇相同工序的項目進行批次操作
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex gap-2">
                {selectedItems.size > 0 && (
                  <>
                    <Button 
                      onClick={handleBatchStart}
                      className="min-h-[36px]"
                    >
                      <Play className="mr-2 h-4 w-4" />
                      同時開始
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={clearSelection}
                      className="min-h-[36px]"
                    >
                      清空選擇
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Desktop Resizable Panel Layout */}
      <div className="hidden md:block flex-1 px-6 pb-6">
        <ResizablePanelGroup direction="horizontal" className="h-full">
          {/* Main Calendar Panel */}
          <ResizablePanel defaultSize={showDetailsPanel ? 50 : 100} minSize={40}>
            <Card className="h-full">
              <CardHeader>
                <CardTitle>
                  {selectedDepartment} - {selectedWorkItem ? '當日工作安排' : (showBatchPanel && hasBatchProcessing()) ? '批次處理 - 當日工作' : '週工作安排'}
                </CardTitle>
              </CardHeader>
              <CardContent className="h-full overflow-auto">
                {(() => {
                  const isDetailOpen = Boolean(selectedWorkItem) || (showBatchPanel && hasBatchProcessing());
                  const dates = isDetailOpen 
                    ? [selectedWorkItem ? new Date(selectedWorkItem.date) : getBatchDate()] 
                    : weekDates;
                    
                  return (
                    <div
                      className={`grid grid-rows-[auto_1fr] gap-y-3 ${
                        isDetailOpen ? 'grid-cols-1' : 'grid-cols-5 gap-x-4'
                      } h-full`}
                    >
                      {/* Row 0 - 日期標題 + 灰線 */}
                      {dates.map((date, index) => {
                        const isToday = date.toDateString() === new Date().toDateString();
                        const dayNames = ['週日', '週一', '週二', '週三', '週四', '週五', '週六'];
                        const dayName = dayNames[date.getDay()];
                        
                        return (
                          <div key={index} className="flex flex-col items-center pt-1">
                            <div className={`text-sm ${isToday ? 'text-primary font-semibold' : 'text-muted-foreground'}`}>
                              {dayName}
                            </div>
                            <div className={`text-2xl font-bold ${isToday ? 'text-primary' : ''}`}>
                              {date.getDate()}
                            </div>
                            <div className={`text-xs ${isToday ? 'text-primary' : 'text-muted-foreground'}`}>
                              {date.getMonth() + 1}月
                              {isToday && <span className="ml-1 text-xs font-medium">(今天)</span>}
                            </div>
                            
                            {/* 固定高度的間距 + 水平線 */}
                            <div className="h-4" />
                            <div className="w-full h-px bg-border" />
                          </div>
                        );
                      })}

                      {/* Row 1 - 工作項目（緊貼灰線） */}
                      {dates.map((date, index) => {
                        const dayWorkItems = getWorkItemsForDateFiltered(date);
                        const isToday = date.toDateString() === new Date().toDateString();
                        
                        return (
                          <div key={index} className={`space-y-3 overflow-y-auto ${!isDetailOpen ? 'min-h-[300px]' : ''} ${isToday ? 'bg-primary/5' : ''}`}>
                            {/* 顯示空狀態 */}
                            {dayWorkItems.length === 0 && (
                              <div className="py-8 text-center text-xs text-muted-foreground">
                                無工作安排
                              </div>
                            )}
                        {dayWorkItems.map((item) => {
                          const canSelect = canSelectItem(item);
                          const isSelected = selectedItems.has(item.id);
                          const isDisabled = batchMode && !canSelect;
                          
                          return (
                            <Card 
                              key={item.id}
                              className={`transition-all duration-200 ${
                                isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:shadow-md'
                              } ${selectedWorkItem?.id === item.id ? 'bg-primary/10 border-primary/30' : ''} ${
                                isSelected ? 'bg-primary/5 border-primary/30' : 'hover:bg-accent/50'
                              }`}
                              onClick={() => {
                                if (isDisabled) return;
                                if (!batchMode) {
                                  handleWorkItemSelect(item);
                                }
                              }}
                            >
                              <CardContent className="p-4">
                                <div className="flex items-start justify-between mb-3">
                                  <div className="flex items-center gap-3">
                                    {batchMode && (
                                      <Checkbox
                                        checked={isSelected}
                                        disabled={!canSelect}
                                        onCheckedChange={(checked) => {
                                          handleItemSelect(item, checked as boolean);
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                    )}
                                    <Badge 
                                      className={`text-xs ${item.status === 'in-progress' ? 'bg-green-500' : 'bg-yellow-500'} text-white`}
                                    >
                                      {item.status === 'in-progress' ? '進行中' : '待開始'}
                                    </Badge>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    {!batchMode && (
                                      <Button
                                        size="sm"
                                        variant={activeTimers[item.id] ? "destructive" : "outline"}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleTimer(item.id);
                                        }}
                                        className="h-8 w-8 p-0"
                                      >
                                        {activeTimers[item.id] ? <Square className="h-3 w-3" /> : <Play className="h-3 w-3" />}
                                      </Button>
                                    )}
                                    <span className="text-sm font-semibold">{item.orderId}</span>
                                  </div>
                                </div>
                              
                              {activeTimers[item.id] && (
                                <div className="mb-2 p-2 bg-green-50 rounded border border-green-200">
                                  <div className="flex items-center space-x-2">
                                    <Timer className="h-3 w-3 text-green-600" />
                                    <span className="text-xs text-green-700 font-medium">計時中</span>
                                  </div>
                                </div>
                              )}
                              
                              {(() => {
                                const order = getOrderById(item.orderId);
                                return (
                                  <>
                                    <h4 className="text-sm font-medium mb-2">
                                      {order?.product || item.title}
                                    </h4>
                                    <p className="text-xs text-muted-foreground mb-3">
                                      {order?.customer || item.description}
                                    </p>
                                    
                                    <div className="space-y-2">
                                      <div className="flex items-center justify-between">
                                        <span className="text-xs text-blue-600 font-medium">
                                          {(() => {
                                            const order = getOrderById(item.orderId);
                                            return order ? `${order.quantity} 件` : '數量未知';
                                          })()}
                                        </span>
                                        <span className="text-xs text-blue-600 font-medium">
                                          {item.title}
                                        </span>
                                      </div>
                                    </div>
                                  </>
                                );
                              })()}
                              
                            </CardContent>
                          </Card>
                        );
                        })}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          </ResizablePanel>

          {/* Details Panel */}
          {showDetailsPanel && (
            <>
              <ResizableHandle withHandle />
              <ResizablePanel defaultSize={50} minSize={40} maxSize={60}>
                <Card className="h-full">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle>
                      {hasBatchProcessing() && showBatchPanel ? '批次處理面板' : '工作項目詳情'}
                    </CardTitle>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleCloseDetails}
                      className="h-6 w-6 p-0"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </CardHeader>
                  
                  {hasBatchProcessing() && showBatchPanel ? (
                    // 批次處理面板
                    <CardContent className="space-y-4 h-full overflow-auto">
                      {/* 批次處理標題 */}
                      <div className="bg-primary/5 p-4 rounded-lg border border-primary/20">
                        <div className="flex items-center gap-3 mb-2">
                          <Layers className="h-5 w-5 text-primary" />
                          <div>
                            <h3 className="font-semibold text-lg">
                              批次處理中: {getOperationDisplayName(getCurrentBatchOperationType() || '')} 工序
                            </h3>
                            <p className="text-sm text-muted-foreground">
                              {getCurrentBatchItems().length} 項訂單同時進行
                            </p>
                          </div>
                        </div>
                        
                        {/* 批次操作按鈕 */}
                        <div className="flex gap-2 mt-3">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={pauseBatch}
                          >
                            <Square className="mr-2 h-4 w-4" />
                            暫停全部
                          </Button>
                          <Button 
                            size="sm"
                            onClick={completeBatch}
                          >
                            <CheckSquare className="mr-2 h-4 w-4" />
                            完成全部
                          </Button>
                        </div>
                      </div>

                      {/* 批次項目列表 */}
                      <div className="space-y-3">
                        <h4 className="font-medium">處理中的訂單:</h4>
                        {getCurrentBatchItems().map((item) => (
                          <div key={item.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                              <div>
                                <div className="font-medium text-sm">{item.orderId}</div>
                                <div className="text-xs text-muted-foreground">{item.title}</div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline" 
                                size="sm"
                                onClick={() => toggleTimer(item.id)}
                              >
                                <Square className="h-3 w-3" />
                              </Button>
                              <Button
                                variant="ghost" 
                                size="sm"
                                onClick={() => {
                                  setSelectedWorkItem(item);
                                  setShowBatchPanel(false); // 切換到單項詳情視圖
                                }}
                              >
                                <Eye className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* 統一計時資訊 */}
                      <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                        <div className="flex items-center gap-2 mb-2">
                          <Timer className="h-4 w-4 text-green-600" />
                          <span className="font-medium text-green-900">批次計時資訊</span>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-green-700 font-medium">開始時間:</span>
                            <div className="text-green-800">
                              {getCurrentBatchItems()[0]?.id && startTimes[getCurrentBatchItems()[0].id]?.toLocaleTimeString()}
                            </div>
                          </div>
                          <div>
                            <span className="text-green-700 font-medium">進行時間:</span>
                            <div className="text-green-800 font-mono">
                              {getCurrentBatchItems()[0]?.id && startTimes[getCurrentBatchItems()[0].id] && 
                                Math.floor((Date.now() - startTimes[getCurrentBatchItems()[0].id].getTime()) / 1000 / 60)
                              } 分鐘
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* 批次備註區域 */}
                      <div>
                        <Label htmlFor="batchNotes" className="text-base font-medium">批次工序備註</Label>
                        <Textarea 
                          id="batchNotes"
                          placeholder="輸入工序備註，將同步到所有批次項目..."
                          value={batchNotes[`batch_${getCurrentBatchOperationType()}`] || ''}
                          onChange={(e) => updateBatchNotes(e.target.value)}
                          rows={4}
                          className="mt-2"
                        />
                        <p className="text-xs text-muted-foreground mt-2">
                          ⚠️ 此備註將同步更新到所有 {getCurrentBatchItems().length} 個批次項目
                        </p>
                      </div>
                    </CardContent>
                  ) : selectedWorkItem && (
                    // 單筆訂單詳情面板
                    <CardContent className="space-y-4">
                      {/* 訂單資訊 */}
                      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                        <h3 className="font-medium text-blue-900 mb-2">訂單資訊</h3>
                        <div className="grid grid-cols-1 gap-2 text-sm">
                          <div>
                            <span className="text-blue-700 font-medium">訂單編號:</span>
                            <span className="ml-2 text-blue-800">{selectedWorkItem.orderId}</span>
                          </div>
                          {(() => {
                            const order = getOrderById(selectedWorkItem.orderId);
                            return order ? (
                              <>
                                <div>
                                  <span className="text-blue-700 font-medium">產品名稱:</span>
                                  <span className="ml-2 text-blue-800">{order.product}</span>
                                </div>
                                <div>
                                  <span className="text-blue-700 font-medium">客戶名稱:</span>
                                  <span className="ml-2 text-blue-800">{order.customer}</span>
                                </div>
                                <div>
                                  <span className="text-blue-700 font-medium">訂購數量:</span>
                                  <span className="ml-2 text-blue-800">{order.quantity} 件</span>
                                </div>
                              </>
                            ) : null;
                          })()}
                          
                          {/* 圖面縮圖顯示 - 包含在訂單資訊內 */}
                          {(() => {
                            const order = getOrderById(selectedWorkItem.orderId);
                            return order?.attachments && order.attachments.length > 0 ? (
                              <div className="mt-4 pt-3 border-t border-blue-200">
                                <div className="flex items-center gap-3">
                                  <h4 className="text-sm font-medium text-blue-700">工程圖面</h4>
                                  <Dialog open={showDrawings} onOpenChange={setShowDrawings}>
                                    <DialogTrigger asChild>
                                      <div
                                        className="cursor-pointer group flex items-center gap-2 hover:bg-blue-50 rounded-lg p-1 transition-colors"
                                        onClick={() => {
                                          setSelectedDrawingIndex(0);
                                          setShowDrawings(true);
                                        }}
                                      >
                                        <div className="w-28 h-20 bg-gray-100 rounded border overflow-hidden flex-shrink-0">
                                          <img
                                            src={order.attachments[0]}
                                            alt={`${order.product} 圖面預覽`}
                                            className="w-full h-full object-contain group-hover:scale-105 transition-transform"
                                            onError={(e) => {
                                              (e.target as HTMLImageElement).src = "/placeholder.svg";
                                            }}
                                          />
                                        </div>
                                        <div className="text-xs text-blue-700 font-medium">
                                          {order.attachments.length > 1 ? `共 ${order.attachments.length} 張圖面` : '1 張圖面'}
                                        </div>
                                        <FileImage className="h-3 w-3 text-blue-500" />
                                      </div>
                                    </DialogTrigger>
                                    <DialogContent className="max-w-5xl max-h-[90vh] p-0">
                                      <DialogHeader className="p-6 pb-0">
                                        <DialogTitle className="flex items-center gap-2">
                                          <FileImage className="h-5 w-5" />
                                          {order.product} - 工程圖面
                                        </DialogTitle>
                                      </DialogHeader>
                                      <div className="flex flex-col h-full">
                                        {/* 圖面顯示區域 */}
                                        <div className="flex-1 p-6 flex flex-col items-center justify-center bg-gray-50">
                                          <div className="relative w-full max-w-4xl">
                                            <img
                                              src={order.attachments[selectedDrawingIndex]}
                                              alt={`${order.product} 圖面 ${selectedDrawingIndex + 1}`}
                                              className="w-full h-auto max-h-[65vh] object-contain bg-white rounded-lg border shadow-sm"
                                              onError={(e) => {
                                                (e.target as HTMLImageElement).src = "/placeholder.svg";
                                              }}
                                            />
                                            {/* 圖面標題 */}
                                            <div className="mt-4 text-center">
                                              <h4 className="text-lg font-medium text-gray-900">
                                                {order.product} 圖面 {selectedDrawingIndex + 1}
                                              </h4>
                                              <p className="text-sm text-gray-600 mt-1">
                                                第 {selectedDrawingIndex + 1} 張，共 {order.attachments.length} 張
                                              </p>
                                            </div>
                                          </div>
                                        </div>
                                        
                                        {/* 圖面導航 */}
                                        {order.attachments.length > 1 && (
                                          <div className="p-4 border-t bg-white">
                                            <div className="flex items-center justify-center gap-4">
                                              <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => setSelectedDrawingIndex(Math.max(0, selectedDrawingIndex - 1))}
                                                disabled={selectedDrawingIndex === 0}
                                              >
                                                <ChevronLeft className="h-4 w-4 mr-1" />
                                                上一張
                                              </Button>
                                              
                                              <div className="flex gap-2">
                                                {order.attachments.map((_, index) => (
                                                  <button
                                                    key={index}
                                                    onClick={() => setSelectedDrawingIndex(index)}
                                                    className={`w-3 h-3 rounded-full transition-colors ${
                                                      index === selectedDrawingIndex
                                                        ? 'bg-primary'
                                                        : 'bg-gray-300 hover:bg-gray-400'
                                                    }`}
                                                  />
                                                ))}
                                              </div>
                                              
                                              <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => setSelectedDrawingIndex(Math.min(order.attachments.length - 1, selectedDrawingIndex + 1))}
                                                disabled={selectedDrawingIndex === order.attachments.length - 1}
                                              >
                                                下一張
                                                <ChevronRight className="h-4 w-4 ml-1" />
                                              </Button>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </DialogContent>
                                  </Dialog>
                                </div>
                              </div>
                            ) : null;
                          })()}
                        </div>
                      </div>

                      {/* 部門工序階段 */}
                      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                        <h3 className="font-medium text-gray-900 mb-4">部門工序階段</h3>
                        <div className="relative">
                          {(() => {
                            const departmentWorkItems = getWorkItemsByOrder(selectedWorkItem.orderId)
                              .filter(item => item.department === selectedDepartment)
                              .sort((a, b) => {
                                const aIndex = parseInt(a.title.match(/\d+/)?.[0] || '0');
                                const bIndex = parseInt(b.title.match(/\d+/)?.[0] || '0');
                                return aIndex - bIndex;
                              });
                            
                            return (
                              <div className="flex items-center justify-between relative">
                                {/* 連接線 */}
                                <div className="absolute top-4 left-4 right-4 h-0.5 bg-gray-300 -z-10"></div>
                                
                                {departmentWorkItems.map((item, index) => {
                                  const isActive = item.id === selectedWorkItem.id;
                                  const isCompleted = item.status === 'completed';
                                  const isLast = index === departmentWorkItems.length - 1;
                                  
                                  return (
                                    <div key={item.id} className={`flex flex-col items-center relative ${!isLast ? 'flex-1' : ''}`}>
                                      {/* 步驟圓圈 */}
                                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm relative z-10 ${
                                        isActive ? 'bg-primary text-white ring-4 ring-primary/20' :
                                        isCompleted ? 'bg-green-500 text-white' :
                                        'bg-gray-300 text-gray-600'
                                      }`}>
                                        {isCompleted ? (
                                          <CheckSquare className="h-4 w-4" />
                                        ) : isActive ? (
                                          <div className="w-2 h-2 rounded-full bg-white animate-pulse" />
                                        ) : (
                                          index + 1
                                        )}
                                      </div>
                                      
                                      {/* 步驟資訊 */}
                                      <div className="mt-3 text-center max-w-[120px]">
                                        <div className={`font-medium text-xs leading-tight ${
                                          isActive ? 'text-primary' :
                                          isCompleted ? 'text-green-700' :
                                          'text-gray-500'
                                        }`}>
                                          {item.title}
                                        </div>
                                        <div className="text-xs text-muted-foreground mt-1">
                                          {item.startTime}-{item.endTime}
                                        </div>
                                        {isActive && (
                                          <div className="mt-1">
                                            <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-1 rounded-full">
                                              進行中
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                      
                                      {/* 連接線段（已完成的部分用綠色）*/}
                                      {!isLast && (
                                        <div className={`absolute top-4 left-8 right-0 h-0.5 z-0 ${
                                          isCompleted ? 'bg-green-500' : 'bg-gray-300'
                                        }`}></div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })()}
                        </div>
                      </div>

                      {/* 實作數量輸入 */}
                      <div>
                        <Label htmlFor="actualQuantity">實作數量</Label>
                        <Input 
                          id="actualQuantity"
                          type="number"
                          placeholder="請輸入實際製作數量"
                          value={actualQuantities[selectedWorkItem.id] || ''}
                          onChange={(e) => {
                            const value = parseInt(e.target.value) || 0;
                            setActualQuantities(prev => ({
                              ...prev,
                              [selectedWorkItem.id]: value
                            }));
                          }}
                          className="w-full"
                        />
                        <div className="text-xs text-muted-foreground mt-1">
                          訂購數量: {(() => {
                            const order = orders.find(o => 
                              selectedWorkItem.orderId === o.id || 
                              selectedWorkItem.orderId === `workitem-${o.id}`
                            );
                            return order ? order.quantity : '未知';
                          })()} 件
                        </div>
                      </div>

                      {/* Timer Status */}
                      {activeTimers[selectedWorkItem.id] && (
                        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                          <div className="flex items-center space-x-2 mb-2">
                            <Timer className="h-4 w-4 text-green-600" />
                            <span className="font-medium text-green-900">計時中</span>
                          </div>
                          <div className="text-sm text-green-700">
                            開始時間: {startTimes[selectedWorkItem.id]?.toLocaleTimeString()}
                          </div>
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="startTime">開始時間</Label>
                          <Input 
                            id="startTime" 
                            type="time" 
                            defaultValue={selectedWorkItem.startTime}
                          />
                        </div>
                        <div>
                          <Label htmlFor="endTime">結束時間</Label>
                          <Input 
                            id="endTime" 
                            type="time" 
                            defaultValue={selectedWorkItem.endTime}
                          />
                        </div>
                      </div>
                      
                      <div>
                        <Label htmlFor="workers">分配人員</Label>
                        <Input 
                          id="workers" 
                          defaultValue={selectedWorkItem.assignedWorkers.join(', ')}
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="notes">工作備註</Label>
                        <Textarea 
                          id="notes" 
                          placeholder="請輸入工作備註..."
                          defaultValue={selectedWorkItem.notes}
                          rows={3}
                        />
                      </div>
                      
                    </CardContent>
                  )}
                </Card>
              </ResizablePanel>
            </>
          )}
        </ResizablePanelGroup>
      </div>

      {/* Mobile Single Day Layout */}
      <div className="md:hidden flex-1 px-3 pb-6 space-y-4">
        {/* 當天工作項目列表 */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              {selectedDepartment} - 今日工作
            </CardTitle>
            <CardDescription>
              {singleDate[0].toLocaleDateString('zh-TW', { 
                year: 'numeric',
                month: 'long', 
                day: 'numeric',
                weekday: 'long'
              })}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {getWorkItemsForDateFiltered(singleDate[0]).map((item) => {
                const canSelect = canSelectItem(item);
                const isSelected = selectedItems.has(item.id);
                const isDisabled = batchMode && !canSelect;
                
                return (
                  <Card 
                    key={item.id}
                    className={`transition-shadow border border-border ${
                      isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:shadow-md'
                    } ${isSelected ? 'bg-primary/5 border-primary/30' : ''}`}
                    onClick={() => {
                      if (isDisabled) return;
                      if (!batchMode) {
                        handleWorkItemSelect(item);
                      }
                    }}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          {batchMode && (
                            <Checkbox
                              checked={isSelected}
                              disabled={!canSelect}
                              onCheckedChange={(checked) => {
                                handleItemSelect(item, checked as boolean);
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                          )}
                          <Badge 
                            className={`text-xs ${item.status === 'in-progress' ? 'bg-green-500' : 'bg-yellow-500'} text-white`}
                          >
                            {item.status === 'in-progress' ? '進行中' : '待開始'}
                          </Badge>
                        </div>
                        <div className="flex items-center space-x-2">
                          {!batchMode && (
                            <Button
                              size="sm"
                              variant={activeTimers[item.id] ? "destructive" : "outline"}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleTimer(item.id);
                              }}
                              className="h-10 w-10 p-0"
                            >
                              {activeTimers[item.id] ? <Square className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                            </Button>
                          )}
                          <span className="text-sm font-semibold">{item.orderId}</span>
                        </div>
                      </div>
                    
                    {activeTimers[item.id] && (
                      <div className="mb-3 p-3 bg-green-50 rounded border border-green-200">
                        <div className="flex items-center space-x-2">
                          <Timer className="h-4 w-4 text-green-600" />
                          <span className="text-sm text-green-700 font-medium">計時中</span>
                        </div>
                      </div>
                    )}
                    
                    {(() => {
                      const order = getOrderById(item.orderId);
                      return (
                        <>
                          <h4 className="text-base font-medium mb-2">
                            {order?.product || item.title}
                          </h4>
                          <p className="text-sm text-muted-foreground mb-3">
                            {order?.customer || item.description}
                          </p>
                          
                          <div className="space-y-2 mb-3">
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-blue-600 font-medium">
                                {(() => {
                                  const order = getOrderById(item.orderId);
                                  return order ? `${order.quantity} 件` : '數量未知';
                                })()}
                              </span>
                              <span className="text-sm text-blue-600 font-medium">
                                {item.title}
                              </span>
                            </div>
                          </div>
                        </>
                      );
                    })()}
                    
                  </CardContent>
                </Card>
                );
              })}
              
              {/* 顯示空狀態 */}
              {getWorkItemsForDateFiltered(singleDate[0]).length === 0 && (
                <div className="text-center text-muted-foreground py-12">
                  <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p className="text-base">今日無工作安排</p>
                  <p className="text-sm mt-1">可以使用左右箭頭查看其他日期</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Mobile Details Modal */}
        {selectedWorkItem && showDetailsPanel && (
          <Card className="fixed inset-x-3 top-20 bottom-6 z-50 bg-background shadow-lg overflow-auto">
            <CardHeader className="flex flex-row items-center justify-between sticky top-0 bg-background border-b">
              <CardTitle className="text-lg">工作項目詳情</CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCloseDetails}
                className="h-8 w-8 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            
            <CardContent className="space-y-4 p-4">
              {/* 訂單資訊 */}
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h3 className="font-medium text-blue-900 mb-3">訂單資訊</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-blue-700 font-medium">訂單編號:</span>
                    <span>{selectedWorkItem.orderId}</span>
                  </div>
                  {(() => {
                    const order = getOrderById(selectedWorkItem.orderId);
                    return order ? (
                      <>
                        <div className="flex justify-between">
                          <span className="text-blue-700 font-medium">產品名稱:</span>
                          <span>{order.product}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-blue-700 font-medium">客戶名稱:</span>
                          <span>{order.customer}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-blue-700 font-medium">訂購數量:</span>
                          <span>{order.quantity} 件</span>
                        </div>
                      </>
                    ) : null;
                  })()}
                </div>
              </div>

              {/* 部門工序階段 */}
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <h3 className="font-medium text-gray-900 mb-4">部門工序階段</h3>
                <div className="relative overflow-x-auto">
                  {(() => {
                    const departmentWorkItems = getWorkItemsByOrder(selectedWorkItem.orderId)
                      .filter(item => item.department === selectedDepartment)
                      .sort((a, b) => {
                        const aIndex = parseInt(a.title.match(/\d+/)?.[0] || '0');
                        const bIndex = parseInt(b.title.match(/\d+/)?.[0] || '0');
                        return aIndex - bIndex;
                      });
                    
                    return (
                      <div className="flex items-center justify-between relative min-w-full">
                        {/* 連接線 */}
                        <div className="absolute top-4 left-4 right-4 h-0.5 bg-gray-300 -z-10"></div>
                        
                        {departmentWorkItems.map((item, index) => {
                          const isActive = item.id === selectedWorkItem.id;
                          const isCompleted = item.status === 'completed';
                          const isLast = index === departmentWorkItems.length - 1;
                          
                          return (
                            <div key={item.id} className={`flex flex-col items-center relative ${!isLast ? 'flex-1' : ''} min-w-[100px]`}>
                              {/* 步驟圓圈 */}
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm relative z-10 ${
                                isActive ? 'bg-primary text-white ring-4 ring-primary/20' :
                                isCompleted ? 'bg-green-500 text-white' :
                                'bg-gray-300 text-gray-600'
                              }`}>
                                {isCompleted ? (
                                  <CheckSquare className="h-4 w-4" />
                                ) : isActive ? (
                                  <div className="w-2 h-2 rounded-full bg-white animate-pulse" />
                                ) : (
                                  index + 1
                                )}
                              </div>
                              
                              {/* 步驟資訊 */}
                              <div className="mt-3 text-center px-1">
                                <div className={`font-medium text-xs leading-tight ${
                                  isActive ? 'text-primary' :
                                  isCompleted ? 'text-green-700' :
                                  'text-gray-500'
                                }`}>
                                  {item.title}
                                </div>
                                <div className="text-xs text-muted-foreground mt-1">
                                  {item.startTime}-{item.endTime}
                                </div>
                                {isActive && (
                                  <div className="mt-1">
                                    <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-1 rounded-full">
                                      進行中
                                    </span>
                                  </div>
                                )}
                              </div>
                              
                              {/* 連接線段（已完成的部分用綠色）*/}
                              {!isLast && (
                                <div className={`absolute top-4 left-8 right-0 h-0.5 z-0 ${
                                  isCompleted ? 'bg-green-500' : 'bg-gray-300'
                                }`}></div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Timer Status */}
              {activeTimers[selectedWorkItem.id] && (
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <div className="flex items-center space-x-2 mb-2">
                    <Timer className="h-4 w-4 text-green-600" />
                    <span className="font-medium text-green-900">計時中</span>
                  </div>
                  <div className="text-sm text-green-700">
                    開始時間: {startTimes[selectedWorkItem.id]?.toLocaleTimeString()}
                  </div>
                </div>
              )}

              {/* 指派人員 */}
              <div>
                <Label className="text-base font-medium">指派人員</Label>
                <div className="mt-2 space-y-2">
                  {selectedWorkItem.assignedWorkers.map((worker, index) => (
                    <div key={index} className="flex items-center space-x-2 p-3 bg-gray-50 rounded">
                      <User className="h-4 w-4 text-gray-500" />
                      <span className="text-sm">{worker}</span>
                    </div>
                  ))}
                </div>
              </div>
              
            </CardContent>
          </Card>
        )}
      </div>

      {/* 批次開始確認對話框 */}
      <Dialog open={showBatchConfirm} onOpenChange={setShowBatchConfirm}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>批次開始確認</DialogTitle>
            <DialogDescription>
              您即將同時開始以下工作項目的計時
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-primary/5 p-3 rounded-lg">
              <div className="font-medium text-sm mb-2">
                工序: {getOperationDisplayName(currentOperationType || '')}
              </div>
              <div className="text-sm text-muted-foreground">
                選中訂單: {selectedItems.size} 項
              </div>
            </div>
            
            <div className="max-h-48 overflow-y-auto space-y-2">
              {getSelectedItemsDetails().map((item) => (
                <div key={item.id} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                  <div>
                    <div className="font-medium text-sm">{item.orderId}</div>
                    <div className="text-xs text-muted-foreground">{item.title}</div>
                  </div>
                  <Badge variant="outline" className="text-xs">
                    {item.assignedWorkers.length} 人
                  </Badge>
                </div>
              ))}
            </div>
            
            <div className="bg-yellow-50 border border-yellow-200 p-3 rounded-lg">
              <div className="flex items-center gap-2 text-yellow-800">
                <Timer className="h-4 w-4" />
                <span className="text-sm font-medium">注意</span>
              </div>
              <div className="text-xs text-yellow-700 mt-1">
                這些訂單將同時開始計時，工序內容將同步進行
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBatchConfirm(false)}>
              取消
            </Button>
            <Button onClick={confirmBatchStart}>
              <Play className="mr-2 h-4 w-4" />
              確認開始
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DepartmentCalendar;